import React, { useState } from 'react';
import {
  Rocket,
  PiggyBank,
  Sparkles,
  Receipt,
  Star,
  Link2,
  Users,
  Loader,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RootState } from '../../../reducers';
import { planPricesPerMonth } from '../../../constants/prices';
import { usePlan } from '../../plan/hooks/plan';
import { createOrUpdateSubscription } from '../../plan/thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import planFeatures from '../../plan/upgrade/upgradeView/featureLimits';
import { setAlertMessage } from '../dashboardSlice';

// NOTE: This modal doesn't support weekly subscription terms - only monthly and annual
// TODO: Add weekly support when needed
const UpSellModal = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const source = useSelector(
    (state: RootState) => state.globalModals.actionData.source,
  );

  const {
    term,
    price: currentPrice,
    provider,
    state: subscriptionState,
  } = useSelector((state: RootState) => state.plan.subscription);

  // Check if user is on a free trial
  const isTrialing = subscriptionState === 'trialing';
  const { packageProperties, upgrade } = useSelector(
    (state: RootState) => state.plan,
  );
  const { sourceFeatureKey } = upgrade;
  const currentPricePerMonth =
    term === 'monthly' ? currentPrice : currentPrice / 12;
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { teamPlan, isPlan } = usePlan();

  const [isLoading, setIsLoading] = useState(false);

  // Don't show upsell modal to enterprise users
  if (isPlan(['ENTERPRISE'])) {
    onClose();
    return null;
  }

  // Check if user is subscribed through mobile payment provider
  const isMobileProvider = provider === 'apple' || provider === 'android';

  const dialogType =
    // eslint-disable-next-line no-nested-ternary
    source === 'limits'
      ? 'unlimited'
      : term === 'monthly'
        ? 'annual'
        : 'unlimited';

  // Get the feature info if this is a feature-specific upsell
  const featureInfo =
    sourceFeatureKey && source === 'limits'
      ? planFeatures.BUSINESS?.[sourceFeatureKey]
      : null;

  const dialogContent = [
    {
      type: 'annual',
      title: t('drawer.upsell.annual.title'),
      content: (
        <>
          <p className="mb-6 text-center text-muted-foreground">
            {t('drawer.upsell.annual.subtitle')}
          </p>
          <div className="mb-6 grid gap-6">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                <div className="flex flex-col gap-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <PiggyBank className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.discount.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.annual.features.discount.description',
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.credits.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('drawer.upsell.annual.features.credits.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Receipt className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.billing.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('drawer.upsell.annual.features.billing.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.annual.features.features.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.annual.features.features.description',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ),
      newPrice:
        teamPlan in planPricesPerMonth
          ? planPricesPerMonth[teamPlan as keyof typeof planPricesPerMonth]
              .annual
          : 0,
    },
    {
      type: 'unlimited',
      title: featureInfo
        ? t('drawer.upsell.unlimited.unlockFeature', {
            feature: t(featureInfo.title),
          })
        : t('drawer.upsell.unlimited.title'),
      content: (
        <>
          <p className="mb-6 text-center text-muted-foreground">
            {featureInfo
              ? t(featureInfo.description)
              : t('drawer.upsell.unlimited.subtitle')}
          </p>

          <div className="mb-6 grid gap-6">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                {featureInfo && (
                  <p className="mb-4 text-center text-sm font-medium text-muted-foreground">
                    {t('drawer.upsell.unlimited.alsoGet')}
                  </p>
                )}

                <div className="flex flex-col gap-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t('drawer.upsell.unlimited.features.aiCredits.title')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.unlimited.features.aiCredits.description',
                          {
                            limit: packageProperties?.askAICount || 'limited',
                          },
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Link2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t(
                          'drawer.upsell.unlimited.features.integrations.title',
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.unlimited.features.integrations.description',
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 font-medium">
                        {t(
                          'drawer.upsell.unlimited.features.teamCollaboration.title',
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'drawer.upsell.unlimited.features.teamCollaboration.description',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ),
      newPrice: planPricesPerMonth.BUSINESS[term],
    },
  ];

  const currentDialog = dialogContent.find(
    (dialog) => dialog.type === dialogType,
  );

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const newPlan = dialogType === 'annual' ? teamPlan : 'BUSINESS';
      const newTerm = dialogType === 'annual' ? 'annual' : term;
      const newPrice =
        (currentDialog?.newPrice ?? 0) * (dialogType === 'annual' ? 12 : 1);

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: newPlan,
          term: newTerm,
          totalAmount: newPrice,
        }),
      );
      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      onClose();
      dispatch(
        pushModal({
          type: 'upgradeSuccess',
        }),
      );
    } catch (error) {
      console.error('Upgrade failed:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('drawer.upsell.pricing.error.message', {
            error: error.message,
          }),
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSavings = () => {
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        const monthlySavings =
          (currentPricePerMonth ?? 0) - (currentDialog.newPrice ?? 0);
        const yearlySavings = monthlySavings * 12;
        return (
          <div className="my-6 flex flex-col gap-4">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                <div className="text-center">
                  <p className="mb-1 text-sm font-medium text-green-600 dark:text-green-500">
                    {t('drawer.upsell.pricing.yearlySavings')}
                  </p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-500">
                    ${yearlySavings}
                  </p>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t('drawer.upsell.pricing.currentMonthly')}
                    </span>
                    <span className="text-sm font-medium">
                      {t('drawer.upsell.pricing.perMonth', {
                        price: currentPricePerMonth,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      {t('drawer.upsell.pricing.newMonthly')}
                    </span>
                    <span className="text-sm font-medium">
                      {t('drawer.upsell.pricing.perMonth', {
                        price: currentDialog.newPrice,
                      })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <p className="text-center text-xs text-muted-foreground">
              {t('drawer.upsell.pricing.billedAnnually', {
                price: (currentDialog.newPrice ?? 0) * 12,
              })}
            </p>
          </div>
        );
      }
      if (dialogType === 'unlimited') {
        return (
          <div className="mt-6 flex flex-col gap-2">
            <Card className="border-none bg-muted/50">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-medium">
                    {t('drawer.upsell.pricing.currentPlan')}
                  </span>
                  <div className="text-right">
                    {isTrialing ? (
                      <span className="text-lg font-semibold">
                        {t('drawer.upsell.pricing.freeTrial', 'Free Trial')}
                      </span>
                    ) : (
                      <span className="text-lg font-semibold">
                        ${currentPricePerMonth}
                        <span className="ml-1 text-xs text-muted-foreground">
                          {t('drawer.upsell.pricing.month')}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {t('drawer.upsell.pricing.unlimitedPlan')}
                  </span>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-primary">
                      ${currentDialog.newPrice}
                      <span className="ml-1 text-xs text-muted-foreground">
                        {t('drawer.upsell.pricing.month')}
                      </span>
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            {term === 'annual' && (
              <p className="text-center text-sm text-muted-foreground">
                {isTrialing
                  ? t('drawer.upsell.pricing.billedAnnuallyNoProrate', {
                      price: (currentDialog.newPrice ?? 0) * 12,
                    })
                  : t('drawer.upsell.pricing.billedAnnually', {
                      price: (currentDialog.newPrice ?? 0) * 12,
                    })}
              </p>
            )}
            {isTrialing && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                {t(
                  'drawer.upsell.pricing.trialActivationNotice',
                  'Your trial will end and you will be charged today.',
                )}
              </p>
            )}
          </div>
        );
      }
    }
    return null;
  };

  const getButtonText = () => {
    if (dialogType === 'annual') {
      return t('drawer.upsell.buttons.switchAnnual');
    }
    if (dialogType === 'unlimited') {
      if (isTrialing) {
        return t(
          'drawer.upsell.buttons.activateUnlimited',
          'Activate Unlimited - Pay Today',
        );
      }
      return t('drawer.upsell.buttons.upgradeUnlimited');
    }
    return '';
  };

  const getDialogTitle = () => {
    if (isMobileProvider) {
      return t('drawer.upsell.mobileProvider.title');
    }
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        return t('drawer.upsell.annual.title');
      }
      return currentDialog.title;
    }
    return '';
  };

  const renderMobileProviderMessage = () => {
    const platform = provider === 'apple' ? 'iOS' : 'Android';

    return (
      <div className="mt-6">
        <Card className="border-none bg-muted/50">
          <CardContent className="p-5">
            <div className="text-center">
              <p className="mb-3 text-sm text-muted-foreground">
                {t('drawer.upsell.mobileProvider.message', { platform })}
              </p>
              <p className="text-sm font-medium">
                {t('drawer.upsell.mobileProvider.instruction')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
        </DialogHeader>
        <div className="py-4 overflow-y-auto">
          {currentDialog?.content}
          {isMobileProvider
            ? renderMobileProviderMessage()
            : calculateSavings()}
        </div>
        <DialogFooter className="gap-2">
          {isMobileProvider ? (
            <Button onClick={onClose} className="w-full">
              {t('drawer.upsell.buttons.close')}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                {t('drawer.upsell.buttons.cancel')}
              </Button>
              <Button
                onClick={handleUpgrade}
                disabled={isLoading}
                className="gap-2"
              >
                {isLoading ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4" />
                )}
                {getButtonText()}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpSellModal;
